import { Button, Icon, PanelGroup, PanelItem, Placeholder, Switch } from '@hai/ui-react';
import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Ellipsis } from 'src/js/component/base/ellipsis';
import { VerticalSeparator } from 'src/js/component/base/vertical-separator';
import { useStores } from 'src/js/hook/use-stores';
import { EmptyThumbnail } from './empty-thumbnail';
import { LogoPosition, LogoSettingsViewModel, logoPositionToIcon } from './logos-models';
import { getInterfaceLabelWithDigit, updateLogoConfiguration } from './logos-store';

interface Props {
  models: LogoSettingsViewModel[];
  onEdit: (model: LogoSettingsViewModel, forceDisplayToTrue?: boolean) => any;
  shouldReloadData: () => void;
}

export const LogoSettingsForm = (props: Props) => {
  const { t } = useTranslation();
  const { sessionStore } = useStores();

  const PlaceHolderPage = () => {
    return (
      <div id="logo-settings" className="mt-5 placeholder">
        <PanelGroup>
          {[4].map((number: number, index: number) => (
            <PanelItem key={`${number}-${index}`} panelColor="transparent">
              <Placeholder as="layout" style={{ width: '100%', height: '100px' }} />
            </PanelItem>
          ))}
        </PanelGroup>
      </div>
    );
  };

  const { models: logoSettings, onEdit, shouldReloadData } = props;
  if (logoSettings.length === 0) {
    return <PlaceHolderPage />;
  }

  return (
    <>
      <div id="logo-settings" className="mt-5">
        <PanelGroup>
          {logoSettings.map((item, index) => (
            <PanelItem key={index} panelColor={{ color: item.panelColor, position: 'left' }}>
              <div className="mk-logo-config-panel">
                <span className="offshoot-title">{getInterfaceLabelWithDigit(item, t)}</span>
                <div
                  className={classNames('squareThumbnail', !sessionStore.isUser() && 'cursor-pointer')}
                  onClick={() => {
                    let forceDisplayToTrue = false; // auto-enable by default
                    if (!item.fileName) {
                      forceDisplayToTrue = true; // auto-enable by default
                    }
                    !sessionStore.isUser() && onEdit(item, forceDisplayToTrue);
                  }}
                >
                  {item.url ? <img src={item.url} /> : <EmptyThumbnail />}
                </div>
                <div className="rightSection">
                  {item.fileName ? (
                    <>
                      <div className="text">
                        <div className="filename fs-mask">
                          <Ellipsis>{item.fileName}</Ellipsis>
                        </div>
                        <div className="extraInfo">
                          <Icon iconname="Opacity" color="haiui-gray-07" size="sm3" />
                          <span>{item.opacity}%</span>
                          <VerticalSeparator />
                          <Icon iconname="PlacementScale" color="haiui-gray-07" size="sm3" />
                          <span>{item.scaling}%</span>
                        </div>
                        <div className="extraInfo">
                          <Icon iconname={logoPositionToIcon(item.positioning)} color="haiui-gray-07" size="sm3" />
                          {item.positioning === LogoPosition.LOGO_POS_ABSOLUTE ||
                          item.positioning === LogoPosition.LOGO_POS_RELATIVE ? (
                            <span>
                              X:{item.x}, Y:{item.y}
                            </span>
                          ) : (
                            <span>{t(`logos.enums.positioning.${item.positioning}`)}</span>
                          )}
                        </div>
                      </div>
                      <div className="iconSection">
                        <Switch
                          checked={item.display}
                          disabled={sessionStore.isUser()}
                          onChange={(newValue: boolean) => {
                            updateLogoConfiguration({ ...item, display: newValue }, t).then(() => {
                              shouldReloadData();
                            });
                          }}
                        />
                        {!sessionStore.isUser() && (
                          <Icon iconname="Settings" color="haiui-gray-05" onClick={() => onEdit(item)} />
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="w100p d-flex align-items-end flex-column ">
                      {!sessionStore.isUser() && (
                        <Button
                          onClick={() => {
                            const forceDisplayToTrue = true; // auto-enable by default
                            onEdit(item, forceDisplayToTrue);
                          }}
                        >
                          {t('logos.button.configure')}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </PanelItem>
          ))}

          {logoSettings?.length < 3 &&
            [...Array((3 - logoSettings?.length) % 3)].map((number: number, index: number) => (
              <PanelItem
                key={`${logoSettings?.length + number}-${index}`}
                panelColor="transparent"
                className="mk-logo-panel-filler visibilityHidden"
              >
                <div />
              </PanelItem>
            ))}
        </PanelGroup>
      </div>
    </>
  );
};
